import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  BookUser,
  CalendarRange,
  FileSignature,
  Files,
  Landmark,
  ListOrdered,
  Receipt,
  ScrollText,
  Search,
  ShieldCheck,
  Wallet,
} from 'lucide-react';

export type BsiTabKey =
  | 'home'
  | 'login'
  | 'accounts'
  | 'last30'
  | 'period'
  | 'destinations'
  | 'transfer'
  | 'pending'
  | 'interbank'
  | 'batch_local'
  | 'batch_paya'
  | 'iban'
  | 'receipt'
  | 'cheque';

/** اسلاگ URL برای هر خدمت — `/banks/bsi/:section` */
export const BSI_TAB_SLUGS: Record<Exclude<BsiTabKey, 'home'>, string> = {
  login: 'login',
  accounts: 'accounts',
  last30: 'last30',
  period: 'period',
  destinations: 'destinations',
  transfer: 'transfer',
  pending: 'pending',
  interbank: 'interbank',
  batch_local: 'batch-local',
  batch_paya: 'batch-paya',
  iban: 'iban',
  receipt: 'receipt',
  cheque: 'cheque',
};

const BSI_SLUG_TO_TAB: Record<string, BsiTabKey> = {
  home: 'home',
  login: 'login',
  accounts: 'accounts',
  last30: 'last30',
  period: 'period',
  destinations: 'destinations',
  transfer: 'transfer',
  pending: 'pending',
  interbank: 'interbank',
  'batch-local': 'batch_local',
  batch_local: 'batch_local',
  'batch-paya': 'batch_paya',
  batch_paya: 'batch_paya',
  iban: 'iban',
  receipt: 'receipt',
  cheque: 'cheque',
};

export function bsiServicePath(tab: BsiTabKey): string {
  if (tab === 'home') return '/banks/bsi';
  return `/banks/bsi/${BSI_TAB_SLUGS[tab]}`;
}

export function parseBsiTabSlug(slug?: string | null): BsiTabKey | null {
  if (slug == null || slug === '') return 'home';
  return BSI_SLUG_TO_TAB[slug] ?? null;
}

export function isBsiTabKey(value: string | null | undefined): value is BsiTabKey {
  if (!value) return false;
  return value === 'home' || value in BSI_TAB_SLUGS;
}

export type BsiFlashMessage = { type: 'success' | 'error'; text: string };

export type BsiServiceTile = {
  key: BsiTabKey;
  title: string;
  description: string;
  icon: LucideIcon;
  tone: string;
  requiresSession?: boolean;
  group: 'auth' | 'accounts' | 'transfer' | 'tools';
};

export const BSI_SERVICE_TILES: BsiServiceTile[] = [
  {
    key: 'login',
    title: 'ورود',
    description: 'کپچا، پیامک و اتصال به اینترنت‌بانک',
    icon: ShieldCheck,
    tone: 'from-slate-700 to-slate-900',
    group: 'auth',
  },
  {
    key: 'accounts',
    title: 'حساب‌ها و مانده',
    description: 'خلاصه حساب‌ها و موجودی لحظه‌ای',
    icon: Wallet,
    tone: 'from-emerald-600 to-teal-700',
    requiresSession: true,
    group: 'accounts',
  },
  {
    key: 'last30',
    title: 'سی گردش آخر',
    description: 'آخرین تراکنش‌های حساب',
    icon: ListOrdered,
    tone: 'from-sky-600 to-cyan-700',
    requiresSession: true,
    group: 'accounts',
  },
  {
    key: 'period',
    title: 'گردش دوره‌ای',
    description: 'گزارش گردش در بازه زمانی',
    icon: CalendarRange,
    tone: 'from-blue-600 to-indigo-700',
    requiresSession: true,
    group: 'accounts',
  },
  {
    key: 'destinations',
    title: 'حساب‌های مقصد',
    description: 'لیست مقصدهای ذخیره‌شده در بانک',
    icon: BookUser,
    tone: 'from-stone-600 to-zinc-700',
    requiresSession: true,
    group: 'accounts',
  },
  {
    key: 'transfer',
    title: 'انتقال صادراتی',
    description: 'به دیگران یا بین حساب‌های خود',
    icon: ArrowLeftRight,
    tone: 'from-orange-500 to-amber-600',
    requiresSession: true,
    group: 'transfer',
  },
  {
    key: 'interbank',
    title: 'ساتنا / پایا / پل',
    description: 'انتقال بین‌بانکی با شبا یا شماره کارت',
    icon: Landmark,
    tone: 'from-rose-500 to-orange-600',
    requiresSession: true,
    group: 'transfer',
  },
  {
    key: 'batch_local',
    title: 'صادرات دسته‌ای',
    description: 'انتقال گروهی به حساب‌های صادرات (شماره حساب)',
    icon: Files,
    tone: 'from-orange-600 to-amber-700',
    requiresSession: true,
    group: 'transfer',
  },
  {
    key: 'batch_paya',
    title: 'پایا دسته‌ای',
    description: 'انتقال گروهی با فایل یا چند ردیف',
    icon: Files,
    tone: 'from-amber-500 to-orange-700',
    requiresSession: true,
    group: 'transfer',
  },
  {
    key: 'pending',
    title: 'قابل امضا',
    description: 'تراکنش‌های در انتظار امضای نهایی',
    icon: FileSignature,
    tone: 'from-yellow-600 to-amber-700',
    requiresSession: true,
    group: 'transfer',
  },
  {
    key: 'iban',
    title: 'استعلام شبا',
    description: 'تبدیل شماره حساب به شبا',
    icon: Search,
    tone: 'from-teal-600 to-emerald-700',
    requiresSession: true,
    group: 'tools',
  },
  {
    key: 'receipt',
    title: 'رسید انتقال',
    description: 'استعلام و دانلود PDF / اکسل',
    icon: Receipt,
    tone: 'from-cyan-600 to-sky-700',
    requiresSession: true,
    group: 'tools',
  },
  {
    key: 'cheque',
    title: 'خدمات چک',
    description: 'وضعیت چک، دسته‌چک و مبلغ',
    icon: ScrollText,
    tone: 'from-stone-600 to-neutral-800',
    requiresSession: true,
    group: 'tools',
  },
];

export function isSessionExpiredMessage(text: string): boolean {
  return text.includes('منقضی') || text.includes('وارد شوید') || text.includes('امنیتی');
}

export function defaultJalaliMonthStart(): string {
  try {
    const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find((p) => p.type === 'year')?.value || '1405';
    const m = parts.find((p) => p.type === 'month')?.value || '01';
    return `${y}/${m}/01`;
  } catch {
    return '1405/01/01';
  }
}

export function defaultJalaliToday(): string {
  try {
    return new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .format(new Date())
      .replace(/-/g, '/');
  } catch {
    return '1405/04/25';
  }
}
